import { compose } from "redux";
import { connect } from "react-redux";
import { IconButton, Tooltip } from "@material-ui/core";
import React, { useEffect, useMemo, useCallback } from "react";

import { useModal } from "altus-modal";
import { MultiSelectModal } from "altus-ui-components";

import {
  updateSpecificationCourse,
  specificationCoursesOnLoad,
  specificationCoursesOnUnload,
  createSpecificationCoursesOnSubmit,
  getAvailableCoursesForSpecification,
  deleteSpecificationCourseAskConfirmation,
} from "features/competence.actions";

import {
  getActionDataStateFromState,
  getSummarizedDataStatesFromState,
} from "app/app.selectors";

import {
  getSpecificationsFromState,
  getAvailableCoursesForSpecificationFromState,
} from "features/competence.selectors";

import SwitchField from "app/components/SwitchField";
import CrudBasePage from "app/components/CrudBasePage";
import { ACTIONS, ICONS, CRITICALITY } from "app/app.constants";

import { CREATE_SPECIFICATION_COURSE_FORM } from "features/competence.constants";
import SpecificationCourseListContainer from "features/specification/components/SpecificationCourseListContainer";

const getKey = item => item.get("specificationSkillId");
const getCourseName = course => course.get("name");

const SpecificationCoursesContainer = ({
  dataState,
  breadcrumb,
  specifications,
  dispatchOnLoad,
  specificationId,
  dispatchOnUnload,
  availableCourses,
  getAvailableCoursesDataState,
  dispatchUpdateSpecificationCourse,
  dispatchDeleteSpecificationCourse,
  dispatchCreateSpecificationCourseOnSubmit,
  dispatchGetAvailableCoursesForSpecification,
}) => {
  const [isOpen, toggleModal] = useModal(CREATE_SPECIFICATION_COURSE_FORM.ID);

  useEffect(() => {
    dispatchOnLoad(specificationId);

    return () => dispatchOnUnload();
  }, [specificationId, dispatchOnUnload, dispatchOnLoad]);

  useEffect(() => {
    if (isOpen) {
      dispatchGetAvailableCoursesForSpecification(specificationId);
    }
  }, [specificationId, isOpen, dispatchGetAvailableCoursesForSpecification]);

  const columns = useMemo(
    () => [
      {
        xs: 4,
        title: "Course Name",
        getSortProperty: item => item.getIn(["courseName"]),
      },
      {
        xs: true,
        title: "Group",
        getSortProperty: item => item.getIn(["courseGroupName"]),
      },
      {
        xs: true,
        title: "Type",
        getSortProperty: item => item.getIn(["courseTypeName"]),
      },
      {
        xs: true,
        title: "Valid",
        getSortProperty: item => item.getIn(["courseValidityMonthsString"]),
      },
      {
        xs: true,
        title: "Status",
        getSortProperty: item => item.getIn(["courseStatus"]),
      },
      {
        xs: true,
        justify: "center",
        container: true,
        title: "Criticality",
        getValue: item => (
          <SwitchField
            label="Critical"
            checked={item.get("criticality") === CRITICALITY.CRITICAL}
            onChange={() =>
              dispatchUpdateSpecificationCourse(
                item.get("specificationId"),
                item.get("courseId"),
                item.update("criticality", courseCriticality =>
                  courseCriticality === CRITICALITY.CRITICAL
                    ? CRITICALITY.NOT_CRITICAL
                    : CRITICALITY.CRITICAL,
                ),
              )
            }
          />
        ),
      },
    ],
    [dispatchUpdateSpecificationCourse],
  );

  const actions = useMemo(
    () => [
      {
        getValue: item =>
          item.get("specificationId").toString() ===
            specificationId.toString() && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => dispatchDeleteSpecificationCourse(item)}
              >
                <ICONS.DELETE />
              </IconButton>
            </Tooltip>
          ),
      },
    ],
    [specificationId, dispatchDeleteSpecificationCourse],
  );

  const addCoursesSubmit = useCallback(
    selectedCourses => {
      dispatchCreateSpecificationCourseOnSubmit(
        specificationId,
        selectedCourses.map(course => course.get("courseId")).toArray(),
      ).then(toggleModal);
    },
    [specificationId, toggleModal, dispatchCreateSpecificationCourseOnSubmit],
  );

  return (
    <CrudBasePage
      dataState={dataState}
      title={breadcrumb}
      displayAddButton={true}
      displaySearchField={true}
      addButtonOnClick={toggleModal}
    >
      {specifications.map(specification => {
        const innerSpecificationId = specification.get("specificationId");

        return (
          <SpecificationCourseListContainer
            Icon={ICONS.COURSE}
            key={innerSpecificationId}
            specificationId={innerSpecificationId}
            getKey={getKey}
            actions={actions}
            columns={columns}
            inherited={
              innerSpecificationId.toString() !== specificationId.toString()
            }
          />
        );
      })}
      <MultiSelectModal
        open={isOpen && !getAvailableCoursesDataState.isLoading()}
        displayChips
        onClose={toggleModal}
        getName={getCourseName}
        items={availableCourses}
        title="Add Courses"
        onSubmit={addCoursesSubmit}
      />
    </CrudBasePage>
  );
};

export default compose(
  connect(
    (state, { specificationId }) => ({
      specifications: getSpecificationsFromState(state, specificationId),
      availableCourses: getAvailableCoursesForSpecificationFromState(
        state,
        specificationId,
      ),
      getAvailableCoursesDataState: getActionDataStateFromState(
        state,
        ACTIONS.GET_AVAILABLE_COURSES_FOR_SPECIFICATION,
      ),
      dataState: getSummarizedDataStatesFromState(
        state,
        ACTIONS.GET_SPECIFICATION_COURSES_BY_SPECIFICATION_WITH_PARENTS,
        ACTIONS.GET_AVAILABLE_COURSES_FOR_SPECIFICATION,
        ACTIONS.UPDATE_SPECIFICATION_COURSE,
      ),
    }),
    {
      dispatchOnLoad: specificationCoursesOnLoad,
      dispatchOnUnload: specificationCoursesOnUnload,
      dispatchUpdateSpecificationCourse: updateSpecificationCourse,
      dispatchDeleteSpecificationCourse: deleteSpecificationCourseAskConfirmation,
      dispatchCreateSpecificationCourseOnSubmit: createSpecificationCoursesOnSubmit,
      dispatchGetAvailableCoursesForSpecification: getAvailableCoursesForSpecification,
    },
  ),
)(SpecificationCoursesContainer);
